import { useRef, useState } from 'react'
import { Input } from 'almanac-ui/TextInputField/Input'
import Styles from './NewFileDrawer.module.scss'
import { AlmanacFile, ViewType } from 'types'
import { ProjectCard } from 'components/BlazeCommandLine/components/ProjectCard'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { Drawer } from 'components/Drawer'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Calendar2, Menu6, Table2 } from 'blaze-ui/icons'
import { useCreateFolder } from 'components/BlazeCommandLine/hooks/useCreateFolder'
import { Heading } from 'blaze-ui/Heading/Heading'
import { UserAppPreferencesService } from 'api/services/UserAppPreferencesService'
import { Text } from 'blaze-ui/Text/Text'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { DrawerProps } from 'components/Drawer/Root'

interface Props extends DrawerProps {
  parentFolder?: AlmanacFile | null
  onSuccess?: () => void
}

export const NewProjectDrawer = observer(({ parentFolder, onSuccess, ...props }: Props) => {
  const { drawersStore } = useRootStore()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const [title, setTitle] = useState<string>('')
  const [viewType, setViewType] = useState<ViewType>(ViewType.Calendar)
  const inputRef = useRef<HTMLDivElement>(null)
  const close = () => drawersStore.closeDrawer()

  const { createFolder, isCreating } = useCreateFolder({
    title,
    chosenWorkspaceId: currentWorkspaceId,
    handleClose: () => {
      close()
      UserAppPreferencesService.unlockSidebar()
      onSuccess?.()
    },
    defaultView: viewType,
    chosenParentFolder: parentFolder,
    databaseMode: viewType !== ViewType.List,
    handbookMode: false,
    handleError: () => {
      showErrorMessageToast('Please enter a title')
      inputRef.current && inputRef.current.querySelector('input')?.focus()
    },
  })

  return (
    <Drawer.NestedRoot {...props}>
      <Drawer.Portal>
        <div className={Styles.overlayNested}>
          <Drawer.Overlay />
        </div>
        <Drawer.Content fullHeight>
          <Drawer.InnerContent isSnappedTop>
            <Drawer.Top>
              <Drawer.Back variant="ghost" onPress={close} />
              <Drawer.Title asChild>
                <Heading level={5}>New Project</Heading>
              </Drawer.Title>
              <Drawer.Action variant="ghost" onPress={createFolder} isDisabled={isCreating} position="right">
                Create
              </Drawer.Action>
            </Drawer.Top>
            <Drawer.Container>
              <div className={Styles.label}>
                <Text variant="smallList">Project name</Text>
              </div>
              <div className={Styles.searchWrapper}>
                <Input
                  aria-label="Title"
                  name="title"
                  size="lg"
                  placeholder="Untitled"
                  onChange={(event) => setTitle(event.currentTarget.value)}
                  value={title === 'Untitled' ? '' : title}
                  validationDetails={undefined}
                  validationErrors={undefined}
                />
              </div>

              <div className={Styles.grid}>
                <ProjectCard
                  title="Calendar"
                  description="Event planning and scheduling"
                  icon={Calendar2}
                  color="var(--green-50)"
                  isSelected={viewType === ViewType.Calendar}
                  onClick={() => setViewType(ViewType.Calendar)}
                  dataTestId="calendar-card"
                />
                <ProjectCard
                  title="Table"
                  description="View or store any kind of structured data"
                  icon={Table2}
                  color="var(--orange-50)"
                  isSelected={viewType === ViewType.Table}
                  onClick={() => setViewType(ViewType.Table)}
                  dataTestId="table-card"
                />
                <ProjectCard
                  title="List"
                  description="Simple list of docs and their properties"
                  icon={Menu6}
                  color="var(--magenta-50)"
                  isSelected={viewType === ViewType.List}
                  onClick={() => setViewType(ViewType.List)}
                  dataTestId="list-card"
                />
              </div>
            </Drawer.Container>
          </Drawer.InnerContent>
        </Drawer.Content>
        <Drawer.Overlay />
      </Drawer.Portal>
    </Drawer.NestedRoot>
  )
})
